import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import CommunityTipsTracker from '../CommunityTips/CommunityTipsTracker';

// Registry of available admin tools. Add new tools here and provide a matching
// `render` function — the dashboard automatically lists them as numbered modules.
const ADMIN_TOOLS = [
    {
        id: 'community-tips',
        title: 'Community Tip Result Tracker',
        description: 'View, result and manage tips submitted by the community across every tipper and tool.',
        tag: 'Tracking',
        meta: 'All tippers · Live',
        icon: (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        ),
        render: () => <CommunityTipsTracker />
    }
];

// Faint blueprint grid that gives the console its "control room" atmosphere.
const GRID_BG = {
    backgroundImage:
        'linear-gradient(rgba(148,163,184,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.045) 1px, transparent 1px)',
    backgroundSize: '38px 38px'
};

function AdminTools() {
    const { hasRole } = useAuth();
    const [activeToolId, setActiveToolId] = useState(null);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setMounted(true), 30);
        return () => clearTimeout(t);
    }, []);

    // Hard gate: only ADMIN users can ever reach this view.
    if (!hasRole('ADMIN')) {
        return <Navigate to="/snipe-feed" replace />;
    }

    const activeTool = ADMIN_TOOLS.find(t => t.id === activeToolId) || null;

    // Render the selected tool with a console breadcrumb to return to the grid.
    if (activeTool) {
        return (
            <div className="min-h-screen bg-[#020817]">
                <div className="sticky top-0 z-20 bg-[#020817]/95 backdrop-blur border-b border-slate-800/60">
                    <div className="px-3 md:px-4 py-2.5 flex items-center gap-2.5">
                        <button
                            onClick={() => setActiveToolId(null)}
                            className="group flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-mono font-medium text-slate-400 hover:text-white hover:bg-slate-800/50 transition-colors"
                        >
                            <svg className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                            </svg>
                            <span className="inline-flex items-center gap-1.5">
                                <svg className="w-3.5 h-3.5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                                Admin Console
                            </span>
                        </button>
                        <span className="text-slate-700">/</span>
                        <span className="text-xs font-mono font-semibold text-white truncate">{activeTool.title}</span>
                    </div>
                </div>
                {activeTool.render()}
            </div>
        );
    }

    // Dashboard — the admin "control room".
    return (
        <div className="relative min-h-screen bg-[#020817] overflow-hidden">
            {/* Atmosphere layers */}
            <div className="pointer-events-none absolute inset-0" style={GRID_BG} />
            <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[820px] h-[820px] rounded-full bg-emerald-500/[0.07] blur-[120px]" />
            <div className="pointer-events-none absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/40 to-transparent" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-[#020817] to-transparent" />

            <div className="relative max-w-6xl mx-auto px-3 md:px-6 py-5 md:py-8">
                {/* ===== Section label ===== */}
                <div
                    className={`flex items-center gap-3 mt-7 mb-3.5 transition-all duration-700 delay-100 ${
                        mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                    }`}
                >
                    <div className="w-1 h-3.5 bg-emerald-500" />
                    <h2 className="text-[11px] font-mono font-semibold uppercase tracking-[0.25em] text-slate-400">Available Tools</h2>
                    <div className="flex-1 h-px bg-gradient-to-r from-slate-700/60 to-transparent" />
                    <span className="text-[10px] font-mono text-slate-600 tabular-nums">{String(ADMIN_TOOLS.length).padStart(2, '0')} / {String(ADMIN_TOOLS.length).padStart(2, '0')}</span>
                </div>

                {/* ===== Module grid ===== */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                    {ADMIN_TOOLS.map((tool, index) => (
                        <button
                            key={tool.id}
                            onClick={() => setActiveToolId(tool.id)}
                            style={{ transitionDelay: `${150 + index * 80}ms` }}
                            className={`group relative text-left overflow-hidden rounded-xl border border-slate-700/50 bg-[#0c1424] p-5 transition-all duration-500 hover:border-emerald-500/50 hover:bg-[#0e1a2e] hover:-translate-y-1 hover:shadow-2xl hover:shadow-emerald-950/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50 ${
                                mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                            }`}
                        >
                            {/* hover sheen */}
                            <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-emerald-500/[0.07] via-transparent to-transparent" />
                            {/* corner brackets */}
                            <span className="pointer-events-none absolute top-2 right-2 w-4 h-4 border-t border-r border-slate-700 group-hover:border-emerald-500/60 transition-colors rounded-tr" />
                            <span className="pointer-events-none absolute bottom-2 left-2 w-4 h-4 border-b border-l border-slate-700/0 group-hover:border-emerald-500/40 transition-colors rounded-bl" />

                            <div className="relative">
                                {/* index + tag */}
                                <div className="flex items-center justify-between mb-4">
                                    <span className="text-[11px] font-mono font-bold tabular-nums text-slate-600 group-hover:text-emerald-500/80 transition-colors">
                                        {String(index + 1).padStart(2, '0')}
                                    </span>
                                    {tool.tag && (
                                        <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-slate-500 bg-slate-800/60 border border-slate-700/50 rounded px-1.5 py-0.5">
                                            {tool.tag}
                                        </span>
                                    )}
                                </div>

                                {/* icon */}
                                <div className="w-12 h-12 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 mb-4 group-hover:bg-emerald-500/15 group-hover:scale-105 transition-all duration-300">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        {tool.icon}
                                    </svg>
                                </div>

                                {/* copy */}
                                <h3 className="text-[15px] font-semibold text-white mb-1.5 group-hover:text-emerald-300 transition-colors">{tool.title}</h3>
                                <p className="text-xs text-slate-500 leading-relaxed mb-4 min-h-[32px]">{tool.description}</p>

                                {/* footer */}
                                <div className="flex items-center justify-between pt-3 border-t border-slate-800/70">
                                    {tool.meta && (
                                        <span className="inline-flex items-center gap-1.5 text-[10px] font-mono text-slate-600">
                                            <span className="w-1 h-1 rounded-full bg-emerald-500/70" />
                                            {tool.meta}
                                        </span>
                                    )}
                                    <span className="inline-flex items-center gap-1.5 text-[11px] font-mono font-semibold text-slate-400 group-hover:text-emerald-400 transition-colors ml-auto">
                                        Launch
                                        <svg className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                        </svg>
                                    </span>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default AdminTools;
